'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useCommandCenter } from '@/lib/command-center/CommandCenterContext';
import { useWorkspace } from '@/lib/archimate/WorkspaceContext';
import { ComplianceHealthWidget } from './widgets/ComplianceHealthWidget';
import { EntityCoverageWidget } from './widgets/EntityCoverageWidget';
import { RecentActivityWidget } from './widgets/RecentActivityWidget';
import { CostSummaryWidget } from './widgets/CostSummaryWidget';
import { DocHealthWidget } from './widgets/DocHealthWidget';
import { ReliabilityWidget } from './widgets/ReliabilityWidget';
import ExportPanel from './ExportPanel';
import DashboardSettingsPanel from './DashboardSettingsPanel';
import {
  type DashboardSettings,
  loadDashboardSettings,
} from '@/lib/command-center/dashboard-settings';

interface DashboardStats {
  total_entities: number;
  active_entities: number;
  total_links: number;
  total_mappings: number;
  total_assessments: number;
  pending_matches: number;
  entity_types: number;
}

interface ComplianceScore {
  framework: string;
  tool_id: string;
  score: number;
  last_assessment: string | null;
  status: 'compliant' | 'partial' | 'non-compliant' | 'not-assessed';
}

interface EntityTypeCount {
  entity_type: string;
  count: number;
}

interface EntityCategory {
  category: string;
  count: number;
}

interface ActivityEntry {
  id: string;
  action: string;
  object_type: string;
  object_name: string | null;
  created_at: string;
}

interface MatchSuggestion {
  id: string;
  source_name: string;
  target_name: string;
  confidence: number;
}

interface DashboardData {
  stats: DashboardStats;
  entities_by_type: EntityTypeCount[];
  entities_by_category: EntityCategory[];
  recent_activity: ActivityEntry[];
  pending_matches: MatchSuggestion[];
  compliance_health: ComplianceScore[];
  sync_status: Record<string, number>;
}

export function Dashboard() {
  const { workspaceId, entities, assessments } = useCommandCenter();
  const { currentWorkspace } = useWorkspace();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [dashboardSettings, setDashboardSettings] = useState<DashboardSettings>(() => loadDashboardSettings());

  useEffect(() => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    const fetchDashboard = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch(`/api/secure/command-center/dashboard?workspace_id=${workspaceId}`);
        if (!res.ok) {
          throw new Error('Failed to load dashboard data');
        }
        const dashboardData = await res.json();
        setData(dashboardData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [workspaceId]);

  if (!workspaceId) {
    return null;
  }

  // Helper to check widget visibility
  const isWidgetVisible = (widgetId: string) => {
    const widget = dashboardSettings.widgets.find(w => w.id === widgetId);
    return widget?.visible ?? true;
  };

  const handleSettingsChange = (settings: DashboardSettings) => {
    setDashboardSettings(settings);
  };

  return (
    <div className={dashboardSettings.compactMode ? 'space-y-4' : 'space-y-6'}>
      {/* Dashboard Header */}
      <div className="flex items-center justify-end">
        <button
          onClick={() => setShowSettingsPanel(true)}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-light-text-secondary dark:text-foreground-tertiary hover:text-light-text dark:hover:text-foreground hover:bg-light-surface dark:hover:bg-surface rounded-lg transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Customize
        </button>
      </div>

      {/* Header Stats */}
      {isWidgetVisible('stats') && (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard
          label="Entities"
          value={data?.stats.total_entities ?? 0}
          loading={loading}
          icon="📊"
          href="/command-center/entities3"
        />
        <StatCard
          label="Relationships"
          value={data?.stats.total_links ?? 0}
          loading={loading}
          icon="🔗"
          href="/command-center/relationships"
        />
        <StatCard
          label="ArchiMate Mappings"
          value={data?.stats.total_mappings ?? 0}
          loading={loading}
          icon="🗺️"
        />
        <StatCard
          label="Assessments"
          value={data?.stats.total_assessments ?? 0}
          loading={loading}
          icon="📋"
        />
        <StatCard
          label="Pending Matches"
          value={data?.stats.pending_matches ?? 0}
          loading={loading}
          icon="🔍"
          href="/command-center/matches"
          highlight={data?.stats.pending_matches ? data.stats.pending_matches > 0 : false}
        />
        <StatCard
          label="Entity Types"
          value={data?.stats.entity_types ?? 0}
          loading={loading}
          icon="📁"
        />
      </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}

      {/* Main Dashboard Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Compliance Health */}
        {isWidgetVisible('compliance-health') && (
          <div className="lg:col-span-1">
            <ComplianceHealthWidget
              scores={data?.compliance_health ?? []}
              loading={loading}
            />
          </div>
        )}

        {/* Middle Column - Entity Coverage */}
        {isWidgetVisible('entity-coverage') && (
          <div className="lg:col-span-1">
            <EntityCoverageWidget
              entitiesByType={data?.entities_by_type ?? []}
              entitiesByCategory={data?.entities_by_category ?? []}
              totalEntities={data?.stats.total_entities ?? 0}
              totalMappings={data?.stats.total_mappings ?? 0}
              loading={loading}
            />
          </div>
        )}

        {/* Right Column - Recent Activity */}
        {isWidgetVisible('recent-activity') && (
          <div className="lg:col-span-1">
            <RecentActivityWidget
              activities={data?.recent_activity ?? []}
              loading={loading}
            />
          </div>
        )}
      </div>

      {/* Secondary Widget Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cost Summary Widget */}
        {isWidgetVisible('cost-summary') && workspaceId && (
          <div className="lg:col-span-1">
            <CostSummaryWidget workspaceId={workspaceId} />
          </div>
        )}

        {/* Documentation Health Widget */}
        {isWidgetVisible('doc-health') && workspaceId && (
          <div className="lg:col-span-1">
            <DocHealthWidget workspaceId={workspaceId} />
          </div>
        )}

        {/* Reliability Widget */}
        {isWidgetVisible('reliability') && workspaceId && (
          <div className="lg:col-span-1">
            <ReliabilityWidget workspaceId={workspaceId} />
          </div>
        )}
      </div>

      {/* Pending Matches Alert */}
      {isWidgetVisible('pending-matches') && data?.pending_matches && data.pending_matches.length > 0 && (
        <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-light-text dark:text-foreground">
              Pending Match Suggestions
            </h3>
            <Link href="/command-center/matches" className="text-sm text-accent hover:underline">
              Review All
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.pending_matches.slice(0, 3).map((match) => (
              <div
                key={match.id}
                className="p-3 bg-light-border/50 dark:bg-background rounded-lg"
              >
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-light-text dark:text-foreground truncate">
                    {match.source_name}
                  </span>
                  <span className="text-light-text-secondary dark:text-foreground-tertiary">→</span>
                  <span className="text-light-text dark:text-foreground truncate">
                    {match.target_name}
                  </span>
                </div>
                <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                  {Math.round(match.confidence * 100)}% confidence
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      {isWidgetVisible('quick-actions') && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuickAction
            label="Create Entity"
            icon="➕"
            href="/command-center/entities3"
            description="Add a new entity to your repository"
          />
          <QuickAction
            label="Import"
            icon="📥"
            href="/command-center/import"
            description="Import from files or diagrams"
          />
          <QuickActionButton
            label="Export Data"
            icon="📤"
            description="Download entities, reports"
            onClick={() => setShowExportPanel(true)}
          />
          <QuickAction
            label="Assessments"
            icon="📋"
            href="/command-center/assessments"
            description="View and run assessments"
          />
          <QuickAction
            label="Compliance"
            icon="🛡️"
            href="/command-center/compliance"
            description="Compliance dashboard"
          />
          <QuickAction
            label="Relationships"
            icon="🕸️"
            href="/command-center/relationships"
            description="Explore entity relationships"
          />
          <QuickAction
            label="ArchiMate Sync"
            icon="🔄"
            href="/command-center/sync"
            description="Sync with ArchiMate diagrams"
          />
          <QuickAction
            label="Activity Log"
            icon="📜"
            href="/command-center/activity"
            description="View recent changes"
          />
        </div>
      )}

      {/* Export Panel */}
      <ExportPanel
        isOpen={showExportPanel}
        onClose={() => setShowExportPanel(false)}
        entities={entities}
        assessments={assessments}
        workspaceId={workspaceId}
        workspaceName={currentWorkspace?.name || 'workspace'}
      />

      {/* Dashboard Settings Panel */}
      <DashboardSettingsPanel
        isOpen={showSettingsPanel}
        onClose={() => setShowSettingsPanel(false)}
        onSettingsChange={handleSettingsChange}
      />
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: number;
  loading?: boolean;
  icon: string;
  href?: string;
  highlight?: boolean;
}

function StatCard({ label, value, loading, icon, href, highlight }: StatCardProps) {
  const content = (
    <div
      className={`bg-light-surface dark:bg-surface rounded-lg p-4 transition-all ${
        href ? 'hover:ring-2 hover:ring-ring/50 cursor-pointer' : ''
      } ${highlight ? 'ring-2 ring-ring' : ''}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">{icon}</span>
        <span className="text-xs text-light-text-secondary dark:text-foreground-tertiary uppercase tracking-wide">
          {label}
        </span>
      </div>
      {loading ? (
        <div className="h-8 bg-light-border dark:bg-background rounded animate-pulse" />
      ) : (
        <div className="text-2xl font-bold text-light-text dark:text-foreground">
          {value.toLocaleString()}
        </div>
      )}
    </div>
  );

  if (href) {
    return <Link href={href}>{content}</Link>;
  }

  return content;
}

interface QuickActionProps {
  label: string;
  icon: string;
  href: string;
  description: string;
}

function QuickAction({ label, icon, href, description }: QuickActionProps) {
  return (
    <Link
      href={href}
      className="bg-light-surface dark:bg-surface rounded-lg p-4 hover:ring-2 hover:ring-ring/50 transition-all group"
    >
      <div className="flex items-center gap-3">
        <span className="text-2xl group-hover:scale-110 transition-transform">{icon}</span>
        <div>
          <div className="font-medium text-light-text dark:text-foreground">{label}</div>
          <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary">{description}</div>
        </div>
      </div>
    </Link>
  );
}

interface QuickActionButtonProps {
  label: string;
  icon: string;
  description: string;
  onClick: () => void;
}

function QuickActionButton({ label, icon, description, onClick }: QuickActionButtonProps) {
  return (
    <button
      onClick={onClick}
      className="bg-light-surface dark:bg-surface rounded-lg p-4 hover:ring-2 hover:ring-ring/50 transition-all group text-left w-full"
    >
      <div className="flex items-center gap-3">
        <span className="text-2xl group-hover:scale-110 transition-transform">{icon}</span>
        <div>
          <div className="font-medium text-light-text dark:text-foreground">{label}</div>
          <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary">{description}</div>
        </div>
      </div>
    </button>
  );
}
